/**
 * PcapViewer Component
 *
 * Displays PCAP network capture files using tshark.
 * Shows packet list, protocol stats, and conversation analysis.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type SortingState,
  type ColumnDef,
} from '@tanstack/react-table';
import { api, type PacketSummary, type PcapStats, type ProtocolStat, type Conversation } from '@/services/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Network,
  BarChart3,
  MessageSquare,
  Filter,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface PcapViewerProps {
  path: string;
  filename: string;
  className?: string;
}

type TabType = 'packets' | 'protocols' | 'conversations';

// ============================================
// PcapViewer Component
// ============================================

export function PcapViewer({ path, filename, className }: PcapViewerProps) {
  const [activeTab, setActiveTab] = useState<TabType>('packets');
  const [packets, setPackets] = useState<PacketSummary[]>([]);
  const [stats, setStats] = useState<PcapStats | null>(null);
  const [protocols, setProtocols] = useState<ProtocolStat[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tsharkAvailable, setTsharkAvailable] = useState<boolean | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [displayFilter, setDisplayFilter] = useState('');

  // Check tshark availability and load packets
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);

      try {
        // Check tshark status
        const status = await api.pcap.getStatus();
        setTsharkAvailable(status.available);

        if (!status.available) {
          setError('tshark is not installed on the server. Install Wireshark/tshark to analyze PCAP files.');
          setLoading(false);
          return;
        }

        // Parse PCAP file
        const result = await api.pcap.parse(path);
        setPackets(result.packets);
        setStats(result.stats);
      } catch (err) {
        console.error('Failed to load PCAP:', err);
        setError(err instanceof Error ? err.message : 'Failed to parse PCAP file');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [path]);

  // Load protocols when tab changes
  useEffect(() => {
    if (activeTab === 'protocols' && protocols.length === 0 && tsharkAvailable) {
      api.pcap.getProtocols(path)
        .then(result => setProtocols(result.protocols))
        .catch(err => console.error('Failed to load protocols:', err));
    }
  }, [activeTab, protocols.length, path, tsharkAvailable]);

  // Load conversations when tab changes
  useEffect(() => {
    if (activeTab === 'conversations' && conversations.length === 0 && tsharkAvailable) {
      api.pcap.getConversations(path)
        .then(result => setConversations(result.conversations))
        .catch(err => console.error('Failed to load conversations:', err));
    }
  }, [activeTab, conversations.length, path, tsharkAvailable]);

  // Apply display filter
  const handleApplyFilter = async () => {
    if (!tsharkAvailable) return;

    setLoading(true);
    try {
      const result = await api.pcap.parse(path, { filter: displayFilter || undefined });
      setPackets(result.packets);
      setStats(result.stats);
    } catch (err) {
      console.error('Filter error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Packet table columns
  const packetColumns = useMemo<ColumnDef<PacketSummary>[]>(
    () => [
      {
        accessorKey: 'number',
        header: 'No.',
        cell: ({ getValue }) => (
          <span className="font-mono text-xs">{getValue<number>()}</span>
        ),
        size: 60,
      },
      {
        accessorKey: 'time',
        header: 'Time',
        cell: ({ getValue }) => (
          <span className="font-mono text-xs">{parseFloat(getValue<string>()).toFixed(6)}</span>
        ),
        size: 100,
      },
      {
        accessorKey: 'source',
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-primary-400 transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Source
            <ArrowUpDown className="w-3 h-3" />
          </button>
        ),
        cell: ({ getValue }) => (
          <span className="font-mono text-xs">{getValue<string>()}</span>
        ),
      },
      {
        accessorKey: 'destination',
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-primary-400 transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Destination
            <ArrowUpDown className="w-3 h-3" />
          </button>
        ),
        cell: ({ getValue }) => (
          <span className="font-mono text-xs">{getValue<string>()}</span>
        ),
      },
      {
        accessorKey: 'protocol',
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-primary-400 transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Protocol
            <ArrowUpDown className="w-3 h-3" />
          </button>
        ),
        cell: ({ getValue }) => {
          const protocol = getValue<string>();
          return (
            <span className={cn(
              'px-1.5 py-0.5 rounded text-xs font-medium',
              getProtocolColor(protocol)
            )}>
              {protocol}
            </span>
          );
        },
        size: 80,
      },
      {
        accessorKey: 'length',
        header: 'Length',
        cell: ({ getValue }) => (
          <span className="font-mono text-xs">{getValue<number>()}</span>
        ),
        size: 70,
      },
      {
        accessorKey: 'info',
        header: 'Info',
        cell: ({ getValue }) => (
          <span className="text-xs text-surface-400 truncate">{getValue<string>()}</span>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: packets,
    columns: packetColumns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize: 100 },
    },
  });

  if (loading && tsharkAvailable === null) {
    return (
      <div className={cn('h-full flex items-center justify-center bg-surface-900', className)}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-surface-400">Loading {filename}...</p>
        </div>
      </div>
    );
  }

  if (error || tsharkAvailable === false) {
    return (
      <div className={cn('h-full flex items-center justify-center bg-surface-900', className)}>
        <div className="text-center max-w-md p-6">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <div className="font-medium text-amber-200 mb-2">PCAP Analysis Unavailable</div>
          <div className="text-sm text-surface-400 mb-4">
            {error || 'tshark is not installed on the server.'}
          </div>
          <div className="text-xs text-surface-500">
            To enable PCAP analysis, install Wireshark/tshark on the server.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('h-full flex flex-col bg-surface-900', className)}>
      {/* Tabs */}
      <div className="flex border-b border-surface-700">
        <button
          onClick={() => setActiveTab('packets')}
          className={cn(
            'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'packets'
              ? 'text-primary-400 border-b-2 border-primary-500 bg-primary-500/5'
              : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
          )}
        >
          <Network className="w-4 h-4" />
          Packets
          {stats && (
            <span className="px-1.5 py-0.5 text-xs rounded-full bg-surface-700">
              {stats.displayedPackets}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('protocols')}
          className={cn(
            'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'protocols'
              ? 'text-primary-400 border-b-2 border-primary-500 bg-primary-500/5'
              : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
          )}
        >
          <BarChart3 className="w-4 h-4" />
          Protocols
        </button>
        <button
          onClick={() => setActiveTab('conversations')}
          className={cn(
            'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'conversations'
              ? 'text-primary-400 border-b-2 border-primary-500 bg-primary-500/5'
              : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
          )}
        >
          <MessageSquare className="w-4 h-4" />
          Conversations
        </button>

        <div className="flex-1" />

        {/* Stats */}
        {stats && (
          <div className="flex items-center gap-4 px-4 text-xs text-surface-400">
            <span>Duration: {parseFloat(stats.duration).toFixed(3)}s</span>
            <span>Total: {stats.totalPackets.toLocaleString()} packets</span>
          </div>
        )}
      </div>

      {/* Packets Tab */}
      {activeTab === 'packets' && (
        <>
          {/* Filter bar */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-surface-700 bg-surface-800">
            <Filter className="w-4 h-4 text-surface-400" />
            <input
              type="text"
              placeholder="Display filter (e.g., ip.addr == 192.168.1.1)"
              value={displayFilter}
              onChange={(e) => setDisplayFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilter()}
              className="flex-1 px-3 py-1.5 text-sm bg-surface-700 border border-surface-600 rounded text-surface-200 placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleApplyFilter}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Apply
            </Button>

            {/* Quick search */}
            <input
              type="text"
              placeholder="Search..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="w-48 px-3 py-1.5 text-sm bg-surface-700 border border-surface-600 rounded text-surface-200 placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface-800 z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-3 py-2 text-left font-medium text-surface-300 border-b border-surface-700 whitespace-nowrap"
                        style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, i) => (
                  <tr
                    key={row.id}
                    className={cn(
                      i % 2 === 0 ? 'bg-surface-900' : 'bg-surface-850',
                      'hover:bg-surface-800 transition-colors cursor-pointer'
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3 py-1.5 text-surface-300 border-b border-surface-800 whitespace-nowrap"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-surface-700 bg-surface-800">
            <div className="flex items-center gap-2">
              <span className="text-xs text-surface-400">Rows per page:</span>
              <select
                value={String(table.getState().pagination.pageSize)}
                onChange={(e) => table.setPageSize(Number(e.target.value))}
                className="h-7 px-2 text-xs bg-surface-700 border border-surface-600 rounded text-surface-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {[50, 100, 250, 500].map((size) => (
                  <option key={size} value={String(size)}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-surface-400">
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="h-7 w-7 p-0"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="h-7 w-7 p-0"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Protocols Tab */}
      {activeTab === 'protocols' && (
        <div className="flex-1 overflow-auto p-4">
          {protocols.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
            </div>
          ) : (
            <div className="space-y-2">
              {protocols.map((proto, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-lg bg-surface-800 border border-surface-700"
                >
                  <span className={cn(
                    'px-2 py-1 rounded text-sm font-medium',
                    getProtocolColor(proto.name)
                  )}>
                    {proto.name}
                  </span>
                  <div className="flex items-center gap-6 text-sm text-surface-400">
                    <span>{proto.frames.toLocaleString()} frames</span>
                    <span>{formatBytes(proto.bytes)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Conversations Tab */}
      {activeTab === 'conversations' && (
        <div className="flex-1 overflow-auto p-4">
          {conversations.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
            </div>
          ) : (
            <div className="space-y-2">
              {conversations.map((conv, i) => (
                <div
                  key={i}
                  className="p-3 rounded-lg bg-surface-800 border border-surface-700"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 font-mono text-sm">
                      <span className="text-primary-400">{conv.addressA}</span>
                      <span className="text-surface-500">&lt;-&gt;</span>
                      <span className="text-green-400">{conv.addressB}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-xs text-surface-400">
                    <div>
                      <span className="text-primary-400">{conv.addressA}</span> → {conv.addressB}:
                      <span className="ml-2">{conv.packetsAtoB} pkts, {formatBytes(conv.bytesAtoB)}</span>
                    </div>
                    <div>
                      <span className="text-green-400">{conv.addressB}</span> → {conv.addressA}:
                      <span className="ml-2">{conv.packetsBtoA} pkts, {formatBytes(conv.bytesBtoA)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Helpers
// ============================================

function getProtocolColor(protocol: string): string {
  const proto = protocol.toLowerCase();
  if (proto === 'tcp') return 'bg-blue-500/20 text-blue-300';
  if (proto === 'udp') return 'bg-cyan-500/20 text-cyan-300';
  if (proto === 'http') return 'bg-green-500/20 text-green-300';
  if (proto === 'https' || proto === 'tls') return 'bg-emerald-500/20 text-emerald-300';
  if (proto === 'dns') return 'bg-purple-500/20 text-purple-300';
  if (proto === 'icmp') return 'bg-amber-500/20 text-amber-300';
  if (proto === 'arp') return 'bg-orange-500/20 text-orange-300';
  if (proto === 'sip') return 'bg-pink-500/20 text-pink-300';
  if (proto === 'rtp') return 'bg-rose-500/20 text-rose-300';
  return 'bg-surface-600 text-surface-300';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default PcapViewer;
